import React from 'react';
import { PropTypes } from 'prop-types';
import { Icon } from '@unite-us/ui';
import { browserHistory } from 'common/utils/browserHistory';
import { useFeatureFlag } from 'common/hooks';
import { AUTHORIZATION_STATUSES } from 'src/common/Status/authorizationConstants';
import { ReviewRequestButtonWithModal } from 'common/utils/Workqueue/ReviewRequestButtonWithModal';
import { useAssigneeOptions } from 'common/utils/Workqueue/hooks/useAssigneeOptions';
import { useReviewReasonOptions } from '../hooks/useReviewReasonOptions';
import { useReviewServiceAuthorization } from '../hooks/useUpdateServiceAuthorization';
import AcceptServiceAuthorizationButton from './AcceptServiceAuthorizationButton';
import RejectServiceAuthorizationButton from './RejectServiceAuthorizationButton';
import EditServiceAuthorizationButton from './EditServiceAuthorizationButton';

const ServiceAuthorizationDetailsActionBar = ({ authorization }) => {
  const isOpen = authorization.state === AUTHORIZATION_STATUSES.open;
  const isInReview = authorization.state === AUTHORIZATION_STATUSES.inReview;
  const isAccepted = authorization.state === AUTHORIZATION_STATUSES.accepted;
  const inReviewFeatureEnabled = useFeatureFlag('pays-8984-auth-assign-in-review');
  const updateEditAuthorizationsFeatureEnabled = useFeatureFlag('pays-5650-update-edit-authorizations');
  const canAcceptOrReject = isOpen || (isInReview && inReviewFeatureEnabled);
  const canEdit = isAccepted && updateEditAuthorizationsFeatureEnabled;
  const reviewReasonsOptions = useReviewReasonOptions(
    inReviewFeatureEnabled ? authorization.fee_schedule_program?.fee_schedule?.id : null,
  );
  const assigneeOptions = useAssigneeOptions('service_authorizations', inReviewFeatureEnabled);
  const reviewServiceAuthorization = useReviewServiceAuthorization();

  const handleSubmit = async (data) => reviewServiceAuthorization({
    authorization,
    service_authorization_review_reason: data.review_reason,
    in_review_note: data.in_review_note,
    assignee: data.assignee,
  });

  return (
    <div className="sticky top-0 z-50 flex px-6 py-4 bg-white border-b border-solid border-dark-border-blue">
      <div className="flex-grow">
        <button
          className="flex p-2 border border-solid border-dark-border-blue rounded hover:bg-light-fill-blue"
          type="button"
          onClick={browserHistory.goBack}
        >
          <div className="h-4 w-4">
            <Icon
              className="h-4 w-4 pb-1 stroke-current stroke-1 fill-current text-text-blue"
              icon="IconChevronLeft"
            />
          </div>
          <div className="pl-2 text-text-blue">
            Back
          </div>
        </button>
      </div>
      <div className="flex flex-row space-x-6">
        {isOpen && inReviewFeatureEnabled && (
          <ReviewRequestButtonWithModal
            onSubmit={handleSubmit}
            modalTitle="Review authorization request"
            reviewReasonsOptions={reviewReasonsOptions}
            assigneeOptions={assigneeOptions}
          />
        )}
        {canAcceptOrReject && (
          <>
            <RejectServiceAuthorizationButton authorization={authorization} />
            <AcceptServiceAuthorizationButton
              authorization={authorization}
            />
          </>
        )}
        {canEdit && (
          <EditServiceAuthorizationButton authorization={authorization} />
        )}
      </div>
    </div>
  );
};

ServiceAuthorizationDetailsActionBar.propTypes = {
  authorization: PropTypes.object.isRequired,
};

export default ServiceAuthorizationDetailsActionBar;
